/**
 * Text Layer Renderer
 * Renders text layers with variable binding support
 */

import React from 'react';
import type { TextLayer } from '@/types/layers';
import type { ResolvedPosition } from '@/lib/position/resolver';
import { resolveVariables } from '@/types/template';
import { brushToCss, isGradient } from '@/types/brush';

interface TextLayerRendererProps {
  layer: TextLayer;
  position: ResolvedPosition;
  variableValues: Record<string, string>;
  isSelected?: boolean;
  isHovered?: boolean;
  onClick?: (e: React.MouseEvent) => void;
  onMouseDown?: (e: React.MouseEvent) => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

export function TextLayerRenderer({
  layer,
  position,
  variableValues,
  isSelected,
  isHovered,
  onClick,
  onMouseDown,
  onMouseEnter,
  onMouseLeave,
}: TextLayerRendererProps) {
  const { content, transform, opacity } = layer;

  // Resolve variable bindings in text
  const displayText = content.variableBinding
    ? resolveVariables(content.variableBinding, variableValues)
    : content.text;

  // Handle gradient text with background-clip
  const colorCss = brushToCss(content.color);
  const hasGradient = isGradient(content.color);

  const style: React.CSSProperties = {
    position: 'absolute',
    left: position.x,
    top: position.y,
    width: position.width ?? layer.width ?? 'auto',
    height: position.height ?? layer.height ?? 'auto',
    opacity,
    transform: transform
      ? `rotate(${transform.rotation ?? 0}deg) scale(${transform.scaleX ?? 1}, ${transform.scaleY ?? 1})`
      : undefined,
    transformOrigin: transform
      ? `${(transform.originX ?? 0.5) * 100}% ${(transform.originY ?? 0.5) * 100}%`
      : undefined,
    // Typography
    fontFamily: content.fontFamily,
    fontSize: content.fontSize,
    fontWeight: content.fontWeight,
    // For gradients, use background-clip technique
    ...(hasGradient
      ? {
          background: colorCss,
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
        }
      : { color: colorCss }),
    lineHeight: content.lineHeight ?? 1.2,
    letterSpacing: content.letterSpacing ? `${content.letterSpacing}em` : undefined,
    textAlign: content.textAlign,
    textTransform: content.textTransform,
    maxWidth: content.maxWidth,
    textShadow: content.textShadow,
    // Hover styling (selection handled by SelectionOverlay)
    outline: isHovered && !isSelected ? '1px dashed #3B82F6' : 'none',
    outlineOffset: 2,
    cursor: 'pointer',
    pointerEvents: layer.locked ? 'none' : 'auto',
    userSelect: 'none',
  };

  return (
    <div
      style={style}
      onClick={onClick}
      onMouseDown={onMouseDown}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      data-layer-id={layer.id}
    >
      {displayText}
    </div>
  );
}
